import { useCallback, useEffect, useState } from 'react'
import { Button, Card, Form, Input, Modal, Select, Space, Switch, Table, Tag, message } from 'antd'
import Popconfirm from '../components/Popconfirm'
import * as api from '../lib/api'
import DepartmentManager from './DepartmentManager'
import { useI18n } from '../i18n/I18nProvider'

export default function OrganizationManager () {
  const { t } = useI18n()
  const [businessUnits, setBusinessUnits] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [regions, setRegions] = useState([])
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(true)
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editingBusinessUnit, setEditingBusinessUnit] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm()
  const businessUnitRegions = regions.filter(region => region.organization_structure !== 'department_only')

  const loadBusinessUnits = useCallback(async () => {
    setBusinessUnitsLoading(true)
    try {
      setBusinessUnits(await api.getBusinessUnits())
    } catch (e) {
      message.error(e.message || t('organization.messages.business_units_load_failed', 'Unable to load business units'))
    } finally {
      setBusinessUnitsLoading(false)
    }
  }, [t])

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true)
    try {
      const [organizationOptions, regionOptions] = await Promise.all([
        api.getOrganizationOptions(),
        api.getRegionOptions()
      ])
      setOrganizations(organizationOptions)
      setRegions(regionOptions)
    } catch (e) {
      message.error(e.message || t('organization.messages.load_failed', 'Unable to load organization data'))
    } finally {
      setOptionsLoading(false)
    }
  }, [t])

  useEffect(() => {
    loadBusinessUnits()
    loadOptions()
  }, [loadBusinessUnits, loadOptions])

  function openCreateModal () {
    setEditingBusinessUnit(null)
    form.setFieldsValue({
      name: '',
      code: '',
      organization: organizations.length === 1 ? organizations[0].id : undefined,
      region: businessUnitRegions.length === 1 ? businessUnitRegions[0].id : undefined,
      is_active: true
    })
    setModalOpen(true)
  }

  function openEditModal (businessUnit) {
    setEditingBusinessUnit(businessUnit)
    form.setFieldsValue({
      name: businessUnit.name,
      code: businessUnit.code,
      organization: businessUnit.organization,
      region: businessUnit.region,
      is_active: businessUnit.is_active
    })
    setModalOpen(true)
  }

  async function saveBusinessUnit () {
    const values = await form.validateFields()
    setSaving(true)
    try {
      if (editingBusinessUnit) {
        await api.updateBusinessUnit(editingBusinessUnit.id, values)
        message.success(t('organization.messages.business_unit_saved', 'Business unit saved'))
      } else {
        await api.createBusinessUnit(values)
        message.success(t('organization.messages.business_unit_created', 'Business unit created'))
      }
      setModalOpen(false)
      setEditingBusinessUnit(null)
      await loadBusinessUnits()
    } catch (e) {
      message.error(e.message || t('organization.messages.business_unit_save_failed', 'Unable to save business unit'))
    } finally {
      setSaving(false)
    }
  }

  async function deleteBusinessUnit (businessUnit) {
    try {
      await api.deleteBusinessUnit(businessUnit.id)
      message.success(t('organization.messages.business_unit_deleted', 'Business unit deleted'))
      await loadBusinessUnits()
    } catch (e) {
      message.error(e.message || t('organization.messages.business_unit_delete_failed', 'Unable to delete business unit'))
    }
  }

  const columns = [
    { title: t('organization.table.business_unit', 'Business Unit'), dataIndex: 'name', key: 'name' },
    { title: t('organization.table.code', 'Code'), dataIndex: 'code', key: 'code' },
    { title: t('organization.table.organization', 'Organization'), dataIndex: 'organization_name', key: 'organization_name' },
    { title: t('organization.table.region', 'Region'), dataIndex: 'region_name', key: 'region_name' },
    {
      title: t('organization.table.status', 'Status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: value => value ? <Tag color='green'>{t('common.active', 'Active')}</Tag> : <Tag>{t('common.inactive', 'Inactive')}</Tag>
    },
    {
      title: '',
      key: 'actions',
      width: 170,
      render: (_, record) => (
        <Space size='small'>
          <Button size='small' onClick={() => openEditModal(record)}>{t('common.edit', 'Edit')}</Button>
          <Popconfirm
            title={t('organization.confirm.delete_business_unit', 'Delete business unit?')}
            description={t('organization.confirm.delete_business_unit_description', 'Business units already used by departments, employees, or appraisals cannot be deleted.')}
            okButtonProps={{ danger: true, className: 'tti-btn-danger' }}
            onConfirm={() => deleteBusinessUnit(record)}
          >
            <Button size='small' className='tti-btn-danger'>{t('common.delete', 'Delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Space direction='vertical' size='large' style={{ width: '100%' }}>
      {(optionsLoading || businessUnitRegions.length > 0) && (
        <Card
          size='small'
          title={t('organization.business_units.title', 'Business Units')}
          extra={<Button size='small' className='tti-btn-primary-grey' onClick={openCreateModal} disabled={optionsLoading}>{t('organization.actions.create_business_unit', 'Create Business Unit')}</Button>}
        >
          <Table
            dataSource={businessUnits}
            columns={columns}
            rowKey='id'
            size='small'
            loading={businessUnitsLoading || optionsLoading}
            pagination={false}
          />
        </Card>
      )}

      <DepartmentManager embedded canManage />

      <Modal
        title={editingBusinessUnit
          ? t('organization.actions.edit_business_unit', 'Edit Business Unit')
          : t('organization.actions.create_business_unit', 'Create Business Unit')}
        open={modalOpen}
        onOk={saveBusinessUnit}
        onCancel={() => {
          setModalOpen(false)
          setEditingBusinessUnit(null)
        }}
        okButtonProps={{ loading: saving }}
      >
        <Form form={form} layout='vertical'>
          <Form.Item name='name' label={t('organization.fields.business_unit_name', 'Business Unit Name')} rules={[{ required: true, message: t('organization.validation.business_unit_name_required', 'Business unit name is required') }]}>
            <Input placeholder={t('organization.examples.business_unit_name', 'Power Tools')} />
          </Form.Item>
          <Form.Item
            name='code'
            label={t('organization.fields.code', 'Code')}
            extra={t('organization.fields.code_help', 'Leave blank when creating to generate a code from the name.')}
          >
            <Input placeholder={t('organization.examples.business_unit_code', 'power-tools')} disabled={Boolean(editingBusinessUnit)} />
          </Form.Item>
          <Form.Item name='organization' label={t('organization.fields.organization', 'Organization')} rules={[{ required: true, message: t('organization.validation.organization_required', 'Organization is required') }]}>
            <Select
              options={organizations.map(organization => ({ value: organization.id, label: organization.name }))}
              placeholder={t('organization.fields.select_organization', 'Select organization')}
            />
          </Form.Item>
          <Form.Item name='region' label={t('organization.fields.region', 'Region')} rules={[{ required: true, message: t('organization.validation.region_required', 'Region is required') }]}>
            <Select
              options={businessUnitRegions.map(region => ({ value: region.id, label: region.name }))}
              placeholder={t('organization.fields.select_region', 'Select region')}
            />
          </Form.Item>
          <Form.Item name='is_active' label={t('common.active', 'Active')} valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}
